import React, { useState } from 'react';
import { t } from '../i18n';
import { fmtDate, fmtDay, fmtMoney } from '../lib/format';
import { paidTotal, balanceOf, ledgerStatusTag } from '../lib/ledger';

// One row of the Vendor Ledger, rendered as a card. Shared by the admin
// dashboard and the technician portal — `role` decides which actions show.
// Admin-only labels stay English (the admin dashboard isn't translated);
// anything a technician can see goes through t().
export default function LedgerCard({ entry, role, onRecordPayment, onDeletePayment, onAddProgress, onEdit, onDelete }) {
  const [showProgress, setShowProgress] = useState(false);
  const paid = paidTotal(entry);
  const balance = balanceOf(entry);
  const isAdmin = role === 'admin';
  const progress = entry.progress || [];
  const latestPct = [...progress].reverse().find(p => p.percentComplete != null)?.percentComplete;

  const Money = ({ label, value, color }) => (
    <div style={{ flex: 1, minWidth: 90 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: 15, color }}>{fmtMoney(value)}</div>
    </div>
  );

  return (
    <div className="lead-card" style={{ cursor: 'default' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div>
          <div className="lead-name" style={{ marginBottom: 2 }}>
            {isAdmin && <>{entry.vendorId?.businessName || 'Unknown vendor'} · </>}
            <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{entry.invoiceNumber}</span>
          </div>
          {entry.serviceAddress && <div className="lead-job">{entry.serviceAddress}</div>}
        </div>
        {ledgerStatusTag(entry.status)}
      </div>

      {entry.jobDetails && (
        <div style={{ fontSize: 13, color: 'var(--slate)', margin: '6px 0', lineHeight: 1.4 }}>{entry.jobDetails}</div>
      )}

      {/* Invoice / Paid / Balance — the money columns from the spreadsheet */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '10px 0', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', margin: '8px 0' }}>
        <Money label={t('lg.invoice')} value={entry.invoiceAmount} color="var(--navy)" />
        <Money label={t('lg.paid')} value={paid} color="var(--green)" />
        <Money label={t('lg.balance')} value={balance} color={balance > 0 ? 'var(--red)' : 'var(--green)'} />
      </div>

      {latestPct != null && (
        <div style={{ margin: '2px 0 10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            <span>{t('lg.jobProgress')}</span><span>{latestPct}%</span>
          </div>
          <div style={{ height: 6, background: 'var(--border)', borderRadius: 50, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${latestPct}%`, background: 'var(--green)', borderRadius: 50 }} />
          </div>
        </div>
      )}

      {/* Payment history */}
      {entry.payments?.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div className="sec-label" style={{ marginBottom: 4 }}>{t('lg.payments')}</div>
          {entry.payments.map((p, i) => (
            <div key={p._id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 0', borderBottom: '1px dashed var(--border)' }}>
              <span style={{ color: 'var(--muted)', minWidth: 74 }}>{fmtDay(p.date)}</span>
              <span className="tag tag-slate">{p.mode}</span>
              {p.reference && <span style={{ color: 'var(--muted)', fontSize: 11 }}>#{p.reference}</span>}
              <span style={{ marginLeft: 'auto', color: 'var(--green)' }}>{fmtMoney(p.amount)}</span>
              {isAdmin && onDeletePayment && (
                <button className="btn btn-ghost btn-sm btn-icon" title="Remove this payment" onClick={() => onDeletePayment(entry, p)}>×</button>
              )}
            </div>
          ))}
          {entry.payments.some(p => p.overrideReason) && (
            <div className="alert alert-amber" style={{ marginTop: 6, fontSize: 12 }}>
              {t('lg.overrideNote')} {entry.payments.filter(p => p.overrideReason).map(p => p.overrideReason).join(' · ')}
            </div>
          )}
        </div>
      )}

      {/* Live progress reports */}
      {progress.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <button className="btn btn-ghost btn-sm" style={{ padding: '2px 0', border: 'none' }} onClick={() => setShowProgress(s => !s)}>
            {showProgress ? '▾' : '▸'} {t('lg.progressReports')} ({progress.length})
          </button>
          {showProgress && (
            <div style={{ borderLeft: '2px solid var(--border)', paddingLeft: 10, marginTop: 6 }}>
              {[...progress].reverse().map((u, i) => (
                <div key={u._id || i} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                    {fmtDate(u.createdAt)}
                    {u.percentComplete != null && <> · <span style={{ color: 'var(--green)' }}>{u.percentComplete}%</span></>}
                    {u.authorRole === 'admin' && <> · QPS</>}
                  </div>
                  {u.note && <div style={{ fontSize: 13, margin: '2px 0 6px', lineHeight: 1.4 }}>{u.note}</div>}
                  {u.photos?.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {u.photos.map((src, pi) => (
                        <img
                          key={pi} src={src} alt="" onClick={() => window.open(src, '_blank')}
                          style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--border)' }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {entry.notes && (
        <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', marginBottom: 8 }}>{entry.notes}</div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {onAddProgress && entry.status !== 'paid' && (
          <button className="btn btn-outline btn-sm" onClick={() => onAddProgress(entry)}>{t('lg.addProgress')}</button>
        )}
        {isAdmin && onRecordPayment && balance > 0 && (
          <button className="btn btn-green btn-sm" onClick={() => onRecordPayment(entry)}>Record Payment</button>
        )}
        {entry.invoiceDocs?.map((d, i) => (
          <button key={i} className="btn btn-outline btn-sm" onClick={() => window.open(d, '_blank')}>{t('lg.invoiceFile')} {i + 1}</button>
        ))}
        {isAdmin && onEdit && <button className="btn btn-outline btn-sm" onClick={() => onEdit(entry)}>Edit</button>}
        {isAdmin && onDelete && <button className="btn btn-ghost btn-sm" onClick={() => onDelete(entry)}>Delete</button>}
      </div>

      <div className="lead-footer">
        <span className="lead-date">{t('lg.created')} {fmtDate(entry.createdAt)}</span>
      </div>
    </div>
  );
}
